import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import useTenantState from './tenant.state';
import { keycloakAuth, KeycloakUserInfo } from '@/utils/auth/keycloak';
import { env } from '@/utils/constants/env';

type Store = {
  logout: () => Promise<void>;
  userProfile?: UserProfile;
  keycloakUserInfo?: KeycloakUserInfo;
  tenants?: Tenant[];
  loadUserProfile: (token: string | undefined, force: boolean) => Promise<void>;
  updateUserProfileLocal: (payload: Partial<UserProfile>) => void;
  loadTenants: (token?: string) => Promise<void>;
  lastProfileUpdate?: Date;
  handleLogin: () => Promise<void>;
  getToken: () => Promise<string | null>; // Get current token from Keycloak
  getValidToken: () => Promise<string | null>;
  handleCallback: () => Promise<void>;
  isLogoutLoading?: boolean;
  currentRole: string;
  setCurrentRole: (role: string) => void;
};
export interface UserProfile {
  id: number;
  name: string;
  email: string;
  public_name: string | null;
  avatar_url: string;
  is_admin: boolean;
  email_verified_at: Date;
  tenant_role: string;
  created_at: Date;
  updated_at: Date;
  tenants: Tenant[];
  trial_ends_at: string | null;
}

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  uuid: string;
  userId: number;
  logoPath: string;
  faviconPath: string;
  role?: string;
}

const useAuthState = create<Store>()(
  persist(
    (set, get) => ({
      currentRole: '',
      setCurrentRole: (role: string) => {
        set({ currentRole: role.toLowerCase() });
      },
      lastProfileUpdate: undefined,
      isLogoutLoading: false,
      keycloakUserInfo: undefined,
      updateUserProfileLocal: (payload) => {
        set(state => ({
          userProfile: state.userProfile
            ? structuredClone({ ...state.userProfile, ...payload })
            : state.userProfile,
        }));
      },
      loadUserProfile: async (token) => {
        // Get token from Keycloak if not provided
        const activeToken = token || await keycloakAuth.getValidToken();
        if (!activeToken) {
          console.warn('No token available for loading user profile');
          return;
        }

        try {
          const userInfo = await keycloakAuth.getUserInfo(activeToken);
          console.log('User profile loaded from backend:', userInfo);

          const userInfoFromKC: UserProfile = {
            id: userInfo.id,
            name: userInfo.name,
            email: userInfo.email,
            public_name: null,
            avatar_url: userInfo.avatar_url ?? '',
            is_admin: false,
            email_verified_at: new Date(),
            tenant_role: userInfo.tenant_role,
            created_at: userInfo.created_at,
            updated_at: userInfo.updated_at,
            tenants: [],
            trial_ends_at: userInfo.trial_ends_at || null,
          };

          set({
            keycloakUserInfo: userInfo,
            lastProfileUpdate: new Date(),
            userProfile: userInfoFromKC,
            currentRole: userInfo.tenant_role.toLowerCase(),
          });
        } catch (error) {
          console.error('Error loading user profile:', error);
          globalThis.location.href = `/login`;
          localStorage.removeItem('auth-storage');
          throw error;
        }
      },

      // Get current token from Keycloak instance
      getToken: async () => {
        return await keycloakAuth.getValidToken() || null;
      },

      getValidToken: async () => {
        // Keycloak manages token refresh internally
        return await keycloakAuth.getValidToken() || null;
      },

      loadTenants: async (token?: string) => {
        const activeToken = token || await keycloakAuth.getValidToken();

        if (!activeToken) {
          console.warn('No token available for loading tenants');
          return;
        }

        try {
          const activeTenant = useTenantState.getState().currentTenant;

          const response = await axios.get(env.apiTasklistBaseUrl + '/api/v1/tasklist/me/tenants', {
            headers: {
              Authorization: `Bearer ${activeToken}`,
              'Content-Type': 'application/json',
              'X-Active-Tenant': activeTenant?.slug,
            },
          });

          if (response.status !== 200) {
            throw new Error('Failed to fetch tenants');
          }

          set({ tenants: response.data.data });
          console.info('Tenants loaded successfully');
        } catch (error) {
          console.error('Error loading tenants:', error);
          set({ tenants: [] });
          throw error;
        }
      },

      handleLogin: async () => {
        // Use keycloak-js login method (handles PKCE internally)
        await keycloakAuth.login(globalThis.location.origin + '/callback');
      },

      logout: async () => {
        set({ isLogoutLoading: true });

        try {
          console.log('Initiating Keycloak logout...');
          await keycloakAuth.logout(undefined, globalThis.location.origin + '/login');

          // Clear state (in production, redirect happens before this executes)
          localStorage.removeItem('initial-tenant-id');
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('auth-token');
          localStorage.removeItem('auth-refresh-token');
          localStorage.removeItem('current-tenant-storage');
          localStorage.removeItem('processes-storage');
          localStorage.removeItem('keycloak-auth');

          set({
            isLogoutLoading: false,
            userProfile: undefined,
            tenants: undefined,
            keycloakUserInfo: undefined,
          });
        } catch (error) {
          console.error('Error during logout:', error);

          // Cleanup on error
          localStorage.removeItem('initial-tenant-id');
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('auth-token');
          localStorage.removeItem('auth-refresh-token');
          localStorage.removeItem('current-tenant-storage');
          localStorage.removeItem('processes-storage');
          localStorage.removeItem('keycloak-auth');

          set({
            isLogoutLoading: false,
            userProfile: undefined,
            tenants: undefined,
            keycloakUserInfo: undefined,
          });

          globalThis.location.href = `/login`;
        }
      },

      handleCallback: async () => {
        try {
          // With keycloak-js, tokens are already managed internally
          const token = await keycloakAuth.getValidToken();

          if (!token) {
            throw new Error('Failed to get token from Keycloak');
          }

          // Load user profile from backend API
          await get().loadUserProfile(token, true);

          // Try to load tenants
          try {
            await get().loadTenants(token);
          } catch (error) {
            console.warn('Tenants not available:', error);
          }
        } catch (error) {
          console.error('Error during callback handling:', error);
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        tenants: state.tenants,
        keycloakUserInfo: state.keycloakUserInfo,
        userProfile: state.userProfile,
        currentRole: state.currentRole,
      }),
    },
  ),
);

export default useAuthState;
